import { useQuery } from '@apollo/client'
import ButtonBase from 'components/partials/button'
import DropdownBase, { Option } from 'components/partials/dropDown'
import { TabProps } from 'components/partials/tabs'
import { GET_ALL_AGENCY_GROUP } from 'queries/admin/agencyGroup'
import React, { useState } from 'react'
import { IAgencyGroup } from '../interface'
import AgencyListTable from './agency-list-table'
import IconInformation from 'assets/images/icon-information.svg'
import axiosInstance from 'network/axiosClient'
import { isAxiosError } from 'axios'
import { STATUS } from 'constants/messages'
import { notify } from 'components/toastify'

interface Props extends TabProps {
  dataInput: {
    currentAgencyGroup: IAgencyGroup
  }
}
const LIMIT = 50

interface IDropdownAgencyGroupOption extends Option {}

const DetailAgencyGroup: React.FC<Props> = ({ dataInput }) => {
  const [hasMoreAgencyGroups, setHasMoreAgencyGroups] = useState(true)
  const [currentAgencyGroup, setCurrentAgencyGroup] = useState<IAgencyGroup>(
    dataInput.currentAgencyGroup,
  )
  const [dropdownAgencyGroupOptions, setDropdownAgencyGroupOptions] = useState<
    IDropdownAgencyGroupOption[]
  >([])
  const [isOpenAddAgencyDialog, setIsOpenAddAgencyDialog] = useState<boolean>(false)
  const [isOpenUploadCSVFileDialog, setIsOpenUploadCSVFileDialog] = useState<boolean>(false)
  const {
    data: agencyGroups,
    fetchMore,
    refetch: refetchGetAgencyGroup,
  } = useQuery(GET_ALL_AGENCY_GROUP, {
    variables: {
      skip: 0,
      take: LIMIT,
    },
    onCompleted: (result) => {
      setDropdownAgencyGroupOptions(convertToDropdownOptionByAgencyGroups(result.getAllAgencyGroup))
      setHasMoreAgencyGroups(result.getAllAgencyGroup.length === LIMIT)
    },
    fetchPolicy: 'network-only',
  })

  const handleRefetchAgencyGroup = () => {
    refetchGetAgencyGroup({ skip: 0, take: LIMIT }).then((result) => {
      setDropdownAgencyGroupOptions(
        convertToDropdownOptionByAgencyGroups(result.data.getAllAgencyGroup),
      )
      setHasMoreAgencyGroups(result.data.getAllAgencyGroup.length === LIMIT)
    })
  }

  const handleFetchMoreAgencyGroup = () => {
    if (!hasMoreAgencyGroups || !agencyGroups) return

    fetchMore({
      variables: {
        skip: agencyGroups.getAllAgencyGroup.length,
        take: LIMIT,
      },
    }).then(({ data: fetchMoreData }) => {
      const newItems = fetchMoreData?.getAllAgencyGroup ?? []
      setDropdownAgencyGroupOptions((prev) =>
        prev.concat(convertToDropdownOptionByAgencyGroups(newItems)),
      )
      setHasMoreAgencyGroups(newItems.length === LIMIT)
    })
  }

  const convertToDropdownOptionByAgencyGroups = (data: IAgencyGroup[]) => {
    const options: IDropdownAgencyGroupOption[] = []

    for (const agencyGroup of data) {
      options.push({ id: agencyGroup.agencyGroupId, name: agencyGroup.agencyGroupName })
    }

    return options
  }

  const onSelectDropdown = async (option: IDropdownAgencyGroupOption) => {
    let agencyGroupId: number
    if (typeof option.id === 'string') {
      agencyGroupId = parseInt(option.id, 10)
    } else {
      agencyGroupId = option.id
    }
    setCurrentAgencyGroup({ ...currentAgencyGroup, agencyGroupId, agencyGroupName: option.name })
  }

  const onClickAddAgencyBtn = () => {
    if (!currentAgencyGroup) return
    setIsOpenAddAgencyDialog(true)
  }

  const onClickDownloadCSVBtn = async () => {
    try {
      // setDisableDownloadBtn(true)
      const response = await axiosInstance.post('/export/agencyGroup/detail', {agencyGroupId: currentAgencyGroup.agencyGroupId}, {
        responseType: 'blob',
      })

      const blob = new Blob([response.data])

      // Create a temporary URL for the Blob
      const url = URL.createObjectURL(blob)

      // Create a download link and initiate the file download
      const downloadLink = document.createElement('a')
      downloadLink.href = url
      downloadLink.download = `${currentAgencyGroup.agencyGroupName}.csv`
      downloadLink.click()
    } catch (error: Error | any) {
      if (isAxiosError(error)) {
        notify(error.message, STATUS.ERROR)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    } finally {
      // setDisableDownloadBtn(false)
    }
  }

  return (
    <div className="px-20 md:px-10">
      <div className="pb-5 flex">
        <img className="h-5" src={IconInformation} alt="Icon-Information" />
        <span className='italic text-sm'>Management agencies in agency group</span>
      </div>
      <div className="flex justify-between space-x-2 pb-3">
        <div className="w-2/5">
          <DropdownBase
            className="w-full"
            options={dropdownAgencyGroupOptions}
            initialValue={currentAgencyGroup?.agencyGroupName ?? ''}
            handleOptions={(option) => onSelectDropdown(option as IDropdownAgencyGroupOption)}
            placeHolder="Select agency group to add or edit"
          />
        </div>
        <div className="space-x-2">
        <ButtonBase
          onClick={() => onClickDownloadCSVBtn()}
          bgDisabled="bg-primary-shade3"
          disabled={!currentAgencyGroup}
        >Download CSV</ButtonBase>
          <ButtonBase
            onClick={() => {
              setIsOpenUploadCSVFileDialog(true)
            }}
            bgDisabled="bg-primary-shade3"
            disabled={!currentAgencyGroup}
          >
            Upload
          </ButtonBase>
          <ButtonBase
            onClick={onClickAddAgencyBtn}
            bgDisabled="bg-primary-shade3"
            disabled={!currentAgencyGroup}
          >
            Add Agency
          </ButtonBase>
        </div>
      </div>

      {currentAgencyGroup && (
        <AgencyListTable
          agencyGroup={currentAgencyGroup}
          isOpenAddAgencyDialog={isOpenAddAgencyDialog}
          handleCloseAddAgencyDialog={() => {
            setIsOpenAddAgencyDialog(false)
          }}
          isOpenUploadCSVFileDialog={isOpenUploadCSVFileDialog}
          handleCloseUploadCSVFileDialog={() => {
            setIsOpenUploadCSVFileDialog(false)
          }}
        />
      )}
    </div>
  )
}

export default DetailAgencyGroup
